import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from 'react-query';
import { useState, useCallback, useEffect } from 'react';
import chatApi from '../api/chatApi';

// TODO: Custom hook for chat state and logic
const useChat = () => {
  const queryClient = useQueryClient();

  // Keskustelulista (infinite scroll)
  const {
    data: conversationsPages,
    isLoading: loadingConversations,
    error: conversationsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchConversations,
  } = useInfiniteQuery(
    ['conversations'],
    ({ pageParam = 1 }) => chatApi.listConversations({ pageParam }),
    {
      getNextPageParam: (lastPage, allPages) => {
        // Oletetaan että API palauttaa totalPages ja currentPage
        if (lastPage.page < lastPage.totalPages) {
          return lastPage.page + 1;
        }
        return undefined;
      },
      retry: 1,
      onError: (error) => {
        console.error('Error fetching conversations:', error);
      }
    }
  );

  // Yhdistä kaikki sivut yhdeksi listaksi
  const conversations = conversationsPages?.pages?.flatMap(p => p.results) || [];

  // Aktiivinen keskustelu
  const [activeConversation, setActiveConversation] = useState(null);

  // Viestit (streamistä)
  const [messages, setMessages] = useState([]);
  const [streamLoading, setStreamLoading] = useState(false);
  const [streamError, setStreamError] = useState(null);

  // Hae viestit kun aktiivinen keskustelu vaihtuu
  useEffect(() => {
    const fetchMessages = async () => {
      if (activeConversation?.id) {
        setStreamLoading(true);
        setStreamError(null);
        try {
          const data = await chatApi.getConversation(activeConversation.id);
          console.log('Haetun keskustelun viestit:', data.messages);
          setMessages(data.messages || []);
        } catch (err) {
          setStreamError('Viestien haku epäonnistui');
        } finally {
          setStreamLoading(false);
        }
      } else {
        setMessages([]);
      }
    };
    fetchMessages();
  }, [activeConversation]);

  // Viestin lähetys ja streamaus
  const sendMessage = useCallback(async (message) => {
    console.log('[useChat] Lähetetään viesti:', message);
    console.log('[useChat] Aktiivinen keskustelu ennen lähetystä:', activeConversation);
    setStreamLoading(true);
    setStreamError(null);
    
    // Lisää käyttäjän viesti
    setMessages((prev) => [...prev, { role: 'user', content: message }]);
    
    try {
      let reader;
      
      if (!activeConversation?.id) {
        console.log('[useChat] Ei aktiivista keskustelua, luodaan uusi...');
        reader = await chatApi.createConversation(message);
      } else {
        console.log('[useChat] Lähetetään viesti olemassa olevaan keskusteluun:', activeConversation.id);
        reader = await chatApi.streamMessages(activeConversation.id, message);
      }
      
      let assistantMessage = '';
      let streamComplete = false;

      await chatApi.readEventStream(reader, (eventType, data) => {
        console.log('[useChat] Received event:', eventType, data);
        if (eventType === 'conversation') {
          console.log('[useChat] conversation-event data:', data);
          // Muunna mahdollinen conversation_id -> id
          const normalized = data.conversation_id ? { ...data, id: data.conversation_id } : data;
          setActiveConversation((prev) => {
            console.log('[useChat] Aktiivinen keskustelu ennen päivitystä:', prev);
            console.log('[useChat] Päivitetään aktiivinen keskustelu:', normalized);
            return normalized;
          });
          // Jos messages on tyhjä (uusi keskustelu), lisää käyttäjän viesti ensimmäiseksi
          setMessages((prev) => {
            if (prev.length === 0 && message) {
              return [{ role: 'user', type: 'user', content: message }];
            }
            return prev;
          });
        }
        if (eventType === 'delta') {
          // Jos delta-viesti on objekti ja status: 'finished_successfully', poista kaikki status-viestit
          if (typeof data === 'object' && data.status === 'finished_successfully') {
            setMessages((prev) => prev.filter((msg) => msg.type !== 'status'));
          }
          if (typeof data === 'string') {
            assistantMessage += data;
            setMessages((prev) => {
              const last = prev[prev.length - 1];
              if (last && last.role === 'assistant' && last.type === 'delta' && !last.done) {
                // Päivitä viimeisintä delta-viestiä
                return [
                  ...prev.slice(0, -1),
                  { ...last, content: assistantMessage }
                ];
              } else {
                // Lisää uusi delta-viesti, jos viimeisin ei ole assistentin delta
                return [
                  ...prev,
                  { role: 'assistant', type: 'delta', content: assistantMessage }
                ];
              }
            });
          }
        } else if (eventType === 'status') {
          // Lisää status-viesti
          setMessages((prev) => [
            ...prev,
            { role: 'system', type: 'status', content: data }
          ]);
        } else if (eventType === 'component') {
          // Lisää komponenttiviesti (esim. talentit)
          setMessages((prev) => [
            ...prev,
            { role: 'assistant', type: 'component', content: data }
          ]);
        } else if (eventType === 'message') {
          // Voit käsitellä message-tyyppisiä eventtejä tarvittaessa
        } else if (eventType === 'tool_call') {
          // Lisää tool call -viesti
          setMessages((prev) => [
            ...prev,
            { 
              role: 'assistant', 
              type: 'tool_call', 
              content: {
                name: data.name,
                args: data.args,
                run_id: data.run_id
              }
            }
          ]);
        } else if (eventType === 'tool_result') {
          // Päivitä tool call tuloksella käyttäen run_id:tä
          setMessages((prev) => {
            const lastToolCall = [...prev].reverse().find(msg => 
              msg.type === 'tool_call' && msg.content.run_id === data.run_id
            );
            let newMessages = prev;
            if (lastToolCall) {
              newMessages = prev.map(msg => {
                if (msg === lastToolCall) {
                  return {
                    ...msg,
                    result: data.result
                  };
                }
                return msg;
              });
            }
            // Parsitaan result objektiksi jos se on string
            let parsedResult = data.result;
            if (typeof parsedResult === 'string') {
              try {
                parsedResult = JSON.parse(parsedResult);
              } catch (e) {
                // ignore
              }
            }
            // Jos result sisältää talentteja (talentSearchAgent-työkalun tulos), lisää uusi component-viesti
            if (
              parsedResult &&
              parsedResult.talents &&
              Array.isArray(parsedResult.talents) &&
              parsedResult.talents.length > 0
            ) {
              newMessages = [
                ...newMessages,
                {
                  role: 'assistant',
                  type: 'component',
                  content: {
                    type: 'talents',
                    talents: parsedResult.talents,
                    justification: parsedResult.justification,
                  }
                }
              ];
            }
            return newMessages;
          });
        } else if (eventType === 'system') {
          // Ohita muut järjestelmäviestit UI:ssa
        }
        // Jos stream complete, merkitse viimeinen assistenttiviesti valmiiksi
        if (data && typeof data === 'object' && (data.status === 'finished_successfully' || data.type === 'message_stream_complete')) {
          streamComplete = true;
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last && last.role === 'assistant') {
              return [
                ...prev.slice(0, -1),
                { ...last, done: true }
              ];
            }
            return prev;
          });
        }
      });

      await refetchConversations();
      console.log('[useChat] Viestin lähetys valmis. Aktiivinen keskustelu nyt:', activeConversation);
    } catch (error) {
      console.error('Failed to send message:', error);
      setStreamError(error.message || 'Viestin lähetys epäonnistui');
      throw error;
    } finally {
      setStreamLoading(false);
    }
  }, [activeConversation, refetchConversations]);

  // Keskustelun valinta (nyt synkroninen)
  const selectConversation = useCallback((conv) => {
    setActiveConversation(conv);
    setStreamError(null);
  }, []);

  return {
    conversations,
    loadingConversations,
    conversationsError,
    activeConversation,
    setActiveConversation: selectConversation,
    messages,
    setMessages,
    streamLoading,
    streamError,
    sendMessage,
    refetchConversations,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

export default useChat; 